import type { MeterState, NowPlaying } from "@ees/shared";

export interface MeterProps {
  name: string;
  channel: 0 | 1;
  meters: MeterState;
}

/**
 * Map a level in dBFS onto a 0..1 bar width.
 *
 * Broadcast meters are read against a -60 dB noise floor rather than 0 dBFS, so
 * a linear scale would leave the useful range squashed into the last few pixels.
 */
export function dbToFraction(db: number): number {
  const floor = -60;
  if (!Number.isFinite(db) || db <= floor) return 0;
  return Math.min(1, (db - floor) / -floor);
}

export function Meter({ name, channel, meters }: MeterProps): JSX.Element {
  const peakDb = meters.peakDb[channel];
  const rmsDb = meters.rmsDb[channel];
  const hot = peakDb > -1;
  const clipping = peakDb > -0.1;

  return (
    <div className="meter">
      <span className="meter__name">{name}</span>
      <div
        className="meter__track"
        role="meter"
        aria-label={`${name} level`}
        aria-valuenow={Math.round(peakDb)}
        aria-valuemin={-60}
        aria-valuemax={0}
      >
        <div className="meter__rms" style={{ width: `${dbToFraction(rmsDb) * 100}%` }} />
        <div
          className={`meter__peak${clipping ? " meter__peak--clip" : hot ? " meter__peak--hot" : ""}`}
          style={{ width: `${dbToFraction(peakDb) * 100}%` }}
        />
      </div>
      <span className="meter__value">{peakDb.toFixed(1)} dB</span>
    </div>
  );
}

export function Meters({ meters }: { meters: MeterState }): JSX.Element {
  const lufs = meters.loudnessLufs;
  const rider = meters.loudnessGainDb;
  return (
    <div className="meters">
      <Meter name="L" channel={0} meters={meters} />
      <Meter name="R" channel={1} meters={meters} />
      <Meter name="MASTER" channel={0} meters={meters} />
      {lufs !== undefined ? (
        <span
          className="meters__lufs"
          title="Programme loudness and the gain the loudness rider is applying"
        >
          {lufs <= -70 ? "silence" : `${lufs.toFixed(1)} LUFS`}
          {rider !== undefined && rider !== 0
            ? ` · rider ${rider > 0 ? "+" : ""}${rider.toFixed(1)} dB`
            : ""}
        </span>
      ) : null}
    </div>
  );
}

export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

export function NowPlayingCard({ item }: { item: NowPlaying | null }): JSX.Element {
  if (!item) {
    return (
      <div className="now">
        <span className="now__label">Now playing</span>
        <p className="empty">Station is off air.</p>
      </div>
    );
  }

  const remaining = item.endsAt > 0 ? (item.endsAt - Date.now()) / 1000 : 0;

  return (
    <div className="now">
      <div className="now__row">
        <span className="now__label">{item.fading ? "Crossfading" : "Now playing"}</span>
        <span className="now__meta">
          {formatClock(Math.max(0, remaining))} / {formatClock((item.endsAt - item.startedAt) / 1000)}
        </span>
      </div>
      <div className="now__row">
        <strong>{item.title}</strong>
        <span className="now__meta">{item.artist ?? "—"}</span>
      </div>
      <div
        className="progress"
        role="progressbar"
        aria-valuenow={Math.round(item.progress * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="progress__fill" style={{ width: `${item.progress * 100}%` }} />
      </div>
    </div>
  );
}